import React, {FC} from 'react';
import {connect} from 'react-redux';

import {RendererOf} from '../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  combinePermissions,
  noPermission,
  PermissionDeniedReasonsEnum,
  PermissionResult,
  yesPermission,
} from '../../src/helpers/permissionHelpers';
import {PermissionsEnum} from '../../src/models/api/roleModel';
import {AppState} from '../../src/store/appState';
import {getHasPermission} from '../../src/store/data/access/accessSelectors';
import {createSelector, makeMapStateToProps, wrapInShallowSelector} from '../helpers/reselectHelpers';
import {useOfflinePermission} from './useOfflinePermission';

/*
 * Props.
 */

export interface PermissionCheckProps {
  /** The access permission to check. If not provided, defaults to granting access permission. */
  permission?: PermissionsEnum;
  /** Are global admins always allowed to perform this action? */
  adminOverride?: boolean;
  /** The context namespaces to check against. */
  namespaces?: ReadonlyArray<string>;
  /** The offline permission. If not provided, defaults to denying permission if we're offline. */
  offlinePermission?: PermissionResult;
  /** The bulk action permission. If not provided, defaults to allowing. */
  bulkActionPermission?: PermissionResult;
  /** The message is sending permission. If not provided, defaults to allowing. */
  messageSendingPermission?: PermissionResult;
}

interface PermissionCheckComponentProps extends PermissionCheckProps {
  accessPermission: PermissionResult;
  children: RendererOf<PermissionResult>;
}

const PermissionCheckComponent: FC<PermissionCheckComponentProps> = (props) => {
  const {accessPermission, offlinePermission, bulkActionPermission, messageSendingPermission} = props;
  const defaultOfflinePermission = useOfflinePermission(false);
  const defaultBulkActionPermission = yesPermission();
  const defaultMessageSendingPermission = yesPermission();

  const permission = combinePermissions(
    accessPermission,
    offlinePermission || defaultOfflinePermission,
    bulkActionPermission || defaultBulkActionPermission,
    messageSendingPermission || defaultMessageSendingPermission,
  );

  return <>{props.children(permission)}</>;
};

/*
 * Selectors.
 */

function getNamespaces(_state: AppState, props: PermissionCheckProps) {
  return props.namespaces;
}

function getPermission(_state: AppState, props: PermissionCheckProps) {
  return props.permission;
}

function getAdminOverride(_state: AppState, props: PermissionCheckProps) {
  return props.adminOverride;
}

function makeGetAccessPermission() {
  const getNamespacesShallow = wrapInShallowSelector(getNamespaces);
  return createSelector(
    [getHasPermission, getNamespacesShallow, getPermission, getAdminOverride],
    (hasPermission, namespaces, permission, adminOverride): PermissionResult => {
      if (!permission) {
        return yesPermission();
      }

      if (!namespaces || namespaces.length === 0) {
        return noPermission(PermissionDeniedReasonsEnum.ADMINISTRATOR_DISABLED);
      }

      if (namespaces.every((namespace) => hasPermission(namespace, permission, adminOverride))) {
        return yesPermission();
      }

      return noPermission(PermissionDeniedReasonsEnum.ADMINISTRATOR_DISABLED);
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<PermissionCheckProps>()(() => ({
  accessPermission: makeGetAccessPermission(),
}));

export const PermissionCheck = connect(mapStateToProps)(PermissionCheckComponent);
